import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IProductOrder, IUseProductSheetNavigationProps } from '../../../config/order-tracker-new-common/types';

/**
 * This custom hook manages navigation and state for a product sheet within an order.
 * It takes props to define the product data and handles opening and closing of product sheets.
 *
 * @param props -
 *   - productsTableData: An array of product orders to be referenced for the current product.
 *
 * @returns An object containing the following properties:
 *   - currentProduct: The currently selected product from the products table.
 *   - productNumber: The selected product order number.
 *   - closeProductSheet: A function to navigate back to the order details page.
 *   - tab: The current product sheet tab.
 *   - switchTab: A function to switch between product sheet tabs using routes.
 */
const useProductSheetNavigation = ({ parentPageRoute, productNumber, tab, productsTableData }: IUseProductSheetNavigationProps) => {
  const [currentProduct, setCurrentProduct] = useState<IProductOrder>();
  const navigate = useNavigate();
  const [scrollValue, setScrollValue] = useState(0);

  const closeProductSheet = () =>
    navigate(parentPageRoute);

  useEffect(() => {
    if (productNumber && tab) {
      // when product sheet is opened
      setScrollValue(window.scrollY);
      scrollTo(window.scrollY);
    } else if (!productNumber && !tab) {
      // when product sheet is closed
      scrollTo(scrollValue);
    }
    const productDetails: IProductOrder = productsTableData?.find(
      (item) => item.productOrderNumber === productNumber
    );
    if (productDetails) { setCurrentProduct(productDetails) }
  }, [productNumber, tab]);

  const scrollTo = (value: number) => setTimeout(() => {
    window.scrollTo(0, value);
  }, 100);

  return {
    currentProduct,
    productNumber,
    closeProductSheet,
    tab
  };
};

export default useProductSheetNavigation;
